import React, { useMemo } from 'react';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import {
  ReportField,
  ReportFlowPages,
  ReportFooter,
  ReportHeader,
  ReportMeta,
  ReportMetaRow,
  ReportNote,
  ReportSignLine,
  ReportSigns,
  ReportTitleRow,
} from '../../../components/ui/Report';
import type { CoeWatchlistResponse, CoeWatchlistRow } from '../types';

function dash(v: React.ReactNode) {
  if (v == null || v === '') return '—';
  return v;
}

function fmtDate(v?: string | null) {
  return v ? formatDateGlobal(v) : '—';
}

function daysLabel(days: number | null | undefined): string {
  if (days == null) return '—';
  if (days < 0) return `${Math.abs(days)}d overdue`;
  return `${days}d`;
}

function rowTone(row: CoeWatchlistRow): 'overdue' | 'soon' | 'ok' {
  if (row.intentionOverdue || (row.daysToCoe != null && row.daysToCoe < 0)) return 'overdue';
  if (row.daysToCoe != null && row.daysToCoe <= 30) return 'soon';
  return 'ok';
}

function TableHead() {
  return (
    <table className="coe-print-table coe-print-table-head">
      <thead>
        <tr>
          <th className="coe-print-col-no">No.</th>
          <th className="coe-print-col-name">Crew</th>
          <th className="coe-print-col-vessel">Vessel</th>
          <th className="coe-print-col-rank">Rank</th>
          <th className="coe-print-col-date">Sign-on</th>
          <th className="coe-print-col-months">Months</th>
          <th className="coe-print-col-date">COE</th>
          <th className="coe-print-col-days">Days to COE</th>
          <th className="coe-print-col-date">Intention</th>
          <th className="coe-print-col-date">Ext. COE</th>
          <th className="coe-print-col-flag">Flag</th>
        </tr>
      </thead>
    </table>
  );
}

function LineRow({ row, index }: { row: CoeWatchlistRow; index: number }) {
  const tone = rowTone(row);
  return (
    <table className={`coe-print-table coe-print-row-table coe-print-row-${tone}`}>
      <tbody>
        <tr>
          <td className="coe-print-col-no">{index + 1}</td>
          <td className="coe-print-col-name">{row.name}</td>
          <td className="coe-print-col-vessel">{dash(row.vessel)}</td>
          <td className="coe-print-col-rank">{dash(row.rank)}</td>
          <td className="coe-print-col-date">{fmtDate(row.signOnDate)}</td>
          <td className="coe-print-col-months">
            {row.monthsOnboard != null ? Math.round(row.monthsOnboard) : '—'}
          </td>
          <td className="coe-print-col-date">{fmtDate(row.coeDate)}</td>
          <td className="coe-print-col-days">{daysLabel(row.daysToCoe)}</td>
          <td className="coe-print-col-date">{fmtDate(row.intentionSignOffDate)}</td>
          <td className="coe-print-col-date">{fmtDate(row.extCoeDate)}</td>
          <td className="coe-print-col-flag">
            {row.intentionOverdue ? (
              <span className="coe-print-pill">Overdue</span>
            ) : tone === 'soon' ? (
              <span className="coe-print-pill">Soon</span>
            ) : (
              '—'
            )}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

export type CoeWatchlistReportProps = {
  data: CoeWatchlistResponse;
  rows: CoeWatchlistRow[];
  filterLabels: {
    vessel?: string | null;
    withinDays: string;
    tab?: string | null;
    search?: string | null;
  };
};

export const CoeWatchlistReport: React.FC<CoeWatchlistReportProps> = ({
  data,
  rows,
  filterLabels,
}) => {
  const summary = data.summary;

  const firstHeader = useMemo(
    () => (
      <div className="coe-print-flow-header">
        <div className="coe-print-accent" aria-hidden />
        <ReportHeader>
          <ReportTitleRow title="COE watchlist" formId="Contract end" />
          <ReportMeta>
            <ReportMetaRow>
              <ReportField label="Vessel" value={dash(filterLabels.vessel || 'All vessels')} />
              <ReportField label="Within days" value={filterLabels.withinDays} />
              <ReportField label="Filter" value={dash(filterLabels.tab || 'All')} />
              <ReportField label="Search" value={dash(filterLabels.search || '—')} />
            </ReportMetaRow>
            <ReportMetaRow>
              <ReportField label="Printed" value={fmtDate(new Date().toISOString().slice(0, 10))} />
              <ReportField label="Shown" value={String(rows.length)} />
              <ReportField label="API window" value={`${data.withinDays}d`} />
              <ReportField label="Sorted by" value="Days to COE" />
            </ReportMetaRow>
          </ReportMeta>
          <div className="coe-print-kpis">
            <div className="coe-print-kpi coe-print-kpi-total">
              <span className="coe-print-kpi-label">Total</span>
              <span className="coe-print-kpi-value">{summary.total}</span>
            </div>
            <div className="coe-print-kpi coe-print-kpi-soon">
              <span className="coe-print-kpi-label">COE in window</span>
              <span className="coe-print-kpi-value">{summary.coeSoon}</span>
            </div>
            <div className="coe-print-kpi coe-print-kpi-overdue">
              <span className="coe-print-kpi-label">Overdue intentions</span>
              <span className="coe-print-kpi-value">{summary.intentionOverdue}</span>
            </div>
            <div className="coe-print-kpi coe-print-kpi-shown">
              <span className="coe-print-kpi-label">Shown</span>
              <span className="coe-print-kpi-value">{rows.length}</span>
            </div>
          </div>
        </ReportHeader>
        <TableHead />
      </div>
    ),
    [data.withinDays, filterLabels, rows.length, summary],
  );

  const continueHeader = useMemo(
    () => (
      <div className="coe-print-flow-header">
        <div className="coe-print-continue-bar">
          <span className="coe-print-continue-title">COE watchlist</span>
          <span className="coe-print-continue-hint">continued</span>
        </div>
        <TableHead />
      </div>
    ),
    [],
  );

  const lastFooter = useMemo(
    () => (
      <ReportFooter>
        <ReportSigns>
          <ReportSignLine label="Prepared by" />
          <ReportSignLine label="Date" />
        </ReportSigns>
        <ReportNote>Contract end / intention watchlist for the selected window.</ReportNote>
      </ReportFooter>
    ),
    [],
  );

  const { items, itemKeys } = useMemo(() => {
    if (rows.length === 0) {
      return {
        items: [
          <table key="empty" className="coe-print-table coe-print-row-table">
            <tbody>
              <tr>
                <td colSpan={11} className="coe-print-empty">
                  No COE watchlist rows for these filters.
                </td>
              </tr>
            </tbody>
          </table>,
        ],
        itemKeys: ['empty'],
      };
    }
    return {
      items: rows.map((row, i) => (
        <LineRow key={row.transactionId} row={row} index={i} />
      )),
      itemKeys: rows.map((row) => row.transactionId),
    };
  }, [rows]);

  return (
    <ReportFlowPages
      className="coe-watchlist-print"
      size="landscape"
      firstHeader={firstHeader}
      continueHeader={continueHeader}
      lastFooter={lastFooter}
      items={items}
      itemKeys={itemKeys}
    />
  );
};

export default CoeWatchlistReport;
